import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Logo } from '@/components/Logo';
import { RegionSelector } from '@/components/RegionSelector';

const SignupPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signup } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  // Region gate — `/signup` (bare) always shows the selector; the
  // regional `/in/signup` and `/uae/signup` render the form directly.
  const path = location.pathname;
  const isScoped = path.startsWith('/in/') || path.startsWith('/india/') || path.startsWith('/uae/');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signup(name, email, password);
      toast.success('Welcome to ASTIIV BASGAT!');
      navigate('/onboarding');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isScoped) {
    return (
      <RegionSelector
        title="Create Your Account"
        subtitle="Select your region to continue"
        buildHref={(code) => (code === 'india' ? '/in/signup' : '/uae/signup')}
        bottomNote={
          <>Already a member? <Link to="/login" className="text-gold hover:underline">Sign in</Link></>
        }
        testIdPrefix="signup-country"
      />
    );
  }

  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="glass-card p-8 md:p-12">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-3">
              <Logo to={null} size="lg" wordmarkClassName="text-gold" testId="signup-logo" />
            </div>
            <p className="text-text-secondary">Begin your luxury lifestyle journey</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="signup-form" autoComplete="off">
            <div>
              <Label htmlFor="name" className="text-text-primary mb-2 block">Full Name</Label>
              <Input
                id="name"
                type="text"
                name="dreamlife_fullname"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                required
                autoComplete="off"
                className="bg-transparent border-b border-white/20 focus:border-gold outline-none rounded-none px-0 py-4 text-lg transition-all placeholder:text-white/20"
                data-testid="signup-name-input"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-text-primary mb-2 block">Email</Label>
              <Input
                id="email"
                type="email"
                name="dreamlife_signup_email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                autoComplete="new-password"
                className="bg-transparent border-b border-white/20 focus:border-gold outline-none rounded-none px-0 py-4 text-lg transition-all placeholder:text-white/20"
                data-testid="signup-email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-text-primary mb-2 block">Password</Label>
              <Input
                id="password"
                type="password"
                name="dreamlife_signup_password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                minLength={6}
                autoComplete="new-password"
                className="bg-transparent border-b border-white/20 focus:border-gold outline-none rounded-none px-0 py-4 text-lg transition-all placeholder:text-white/20"
                data-testid="signup-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gold text-black hover:bg-gold-light transition-all duration-500 uppercase tracking-widest text-sm font-bold py-6 rounded-sm"
              data-testid="signup-submit-btn"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-text-secondary">
              Already have an account?{' '}
              <Link to="/login" className="text-gold hover:text-gold-light transition-colors" data-testid="signup-login-link">
                Sign in
              </Link>
            </p>
          </div>

          <div className="mt-6 text-center">
            <Link to="/" className="text-text-muted hover:text-text-secondary transition-colors text-sm" data-testid="signup-back-link">
              ← Back to home
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default SignupPage;